import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EditIcon, PhoneIcon, MailIcon, BuildingIcon, DownloadIcon, FileSpreadsheetIcon, ContactIcon, CalendarIcon, UserIcon, ArchiveIcon, PowerIcon, RotateCcwIcon, FilterIcon, UsersIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Contact, ContactStatus, ContactSource } from '../../types';
import { exportContactsToExcel } from '../../utils/excelExport';
import { exportContactsToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { useConfirm } from '../../contexts/ConfirmContext';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Contacts() {
  const {
    contacts,
    addContact,
    updateContact,
    deleteContact
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const {
    confirm
  } = useConfirm();
  const readOnly = isReadOnly('contacts');
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  // View Mode State: 'active', 'archived', 'deactivated'
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  // Modals for actions
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    position: '',
    contactStatus: 'nuevo' as ContactStatus,
    contactSource: 'otro' as ContactSource,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Load statuses from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-contacts-status');
      if (saved) {
        setItemStatuses(JSON.parse(saved));
      }
    } catch (e) {
      logger.error('Error loading contact statuses', e);
    }
  }, []);
  // Update status helper
  const updateItemStatus = (id: string, status: 'archived' | 'deactivated' | null) => {
    const newStatuses = {
      ...itemStatuses
    };
    if (status === null) {
      delete newStatuses[id]; // Restore to active
    } else {
      newStatuses[id] = status;
    }
    setItemStatuses(newStatuses);
    localStorage.setItem('savidna-contacts-status', JSON.stringify(newStatuses));
  };
  // Filter contacts based on viewMode and search
  const filteredContacts = contacts.filter(c => {
    // Status filter
    const status = itemStatuses[c.id];
    const matchesStatus = viewMode === 'active' ? !status : status === viewMode;
    if (!matchesStatus) return false;
    // Search filter
    return c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.email?.toLowerCase().includes(searchTerm.toLowerCase()) || (c.contactType || '').toLowerCase().includes(searchTerm.toLowerCase());
  });
  const {
    paginatedItems: paginatedContacts,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredContacts);
  const openNewForm = () => {
    setSelectedContact(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      company: '',
      position: '',
      contactStatus: 'nuevo',
      contactSource: 'otro',
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      phone: contact.phone,
      email: contact.email,
      company: contact.company || '',
      position: contact.position || '',
      contactStatus: contact.contactStatus || 'nuevo',
      contactSource: contact.contactSource || 'otro',
      notes: contact.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  // Action handlers
  const openActionConfirm = (contact: Contact, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedContact(contact);
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'danger' | 'warning' | 'info' | 'primary' = 'primary';
    if (type === 'archive') {
      title = 'Archivar Contacto';
      message = `¿Estás seguro de que deseas archivar a "${contact.name}"? Podrás restaurarlo más tarde.`;
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (type === 'deactivate') {
      title = 'Desactivar Contacto';
      message = `¿Estás seguro de que deseas desactivar a "${contact.name}"? No aparecerá en las listas activas.`;
      confirmText = 'Desactivar';
      variant = 'warning';
    } else if (type === 'delete') {
      title = 'Eliminar Contacto';
      message = `¿Estás seguro de que deseas eliminar permanentemente a "${contact.name}"? Esta acción no se puede deshacer.`;
      confirmText = 'Eliminar';
      variant = 'danger';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: () => {
        if (type === 'archive') {
          updateItemStatus(contact.id, 'archived');
          showToast({
            type: 'info',
            title: 'Contacto archivado',
            message: `${contact.name} fue archivado.`
          });
        } else if (type === 'deactivate') {
          updateItemStatus(contact.id, 'deactivated');
          showToast({
            type: 'warning',
            title: 'Contacto desactivado',
            message: `${contact.name} fue desactivado.`
          });
        } else if (type === 'delete') {
          const name = contact.name;
          deleteContact(contact.id);
          const newStatuses = {
            ...itemStatuses
          };
          delete newStatuses[contact.id];
          setItemStatuses(newStatuses);
          localStorage.setItem('savidna-contacts-status', JSON.stringify(newStatuses));
          showToast({
            type: 'error',
            title: 'Contacto eliminado',
            message: `${name} fue eliminado permanentemente.`
          });
        }
        setSelectedContact(null);
      }
    });
  };
  const handleRestore = (contact: Contact) => {
    updateItemStatus(contact.id, null);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.email) errors.email = 'El correo es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    confirm({
      title: selectedContact ? 'Guardar Cambios' : 'Crear Contacto',
      message: selectedContact ? '¿Estás seguro de que deseas guardar los cambios de este contacto?' : '¿Estás seguro de que deseas registrar a este nuevo contacto?',
      confirmText: 'Guardar',
      onConfirm: () => {
        if (selectedContact) {
          updateContact(selectedContact.id, {
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            company: formData.company,
            position: formData.position,
            contactStatus: formData.contactStatus,
            contactSource: formData.contactSource,
            notes: formData.notes
          });
          showToast({
            type: 'success',
            title: 'Contacto actualizado',
            message: `${formData.name} fue actualizado correctamente.`
          });
        } else {
          addContact({
            ...formData,
            createdBy: user?.name || 'Sistema'
          });
          showToast({
            type: 'success',
            title: 'Contacto creado',
            message: `${formData.name} fue registrado correctamente.`
          });
        }
        setIsFormOpen(false);
      }
    });
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = contacts;
    if (dateFrom && dateTo) {
      dataToExport = contacts.filter(c => {
        const dateStr = new Date(c.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportContactsToExcel(dataToExport);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = contacts;
    if (dateFrom && dateTo) {
      dataToExport = contacts.filter(c => {
        const dateStr = new Date(c.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportContactsToPdf(dataToExport);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateContact(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving contact in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateContact(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating contact in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateContact(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring contact in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  return <div className="space-y-6" data-id="element-2353" data-mp="jrfft98h3wc7">
      {/* Header with Gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 shadow-lg text-white" data-id="element-2354" data-mp="oqs1vrriszdq">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-2355" data-mp="xkwou03j9qnz" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-2356" data-mp="33mqy58ijhre9" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-2357" data-mp="3dbikn14tzjhs" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-2358" data-mp="38l27zj8u41wh">
          <div data-id="element-2359" data-mp="3mc72y819c60h">
            <div className="flex items-center gap-2 mb-1" data-id="element-2360" data-mp="3mbp6baaonps1">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-2361" data-mp="3mebaczyd7n5i">
                <UsersIcon className="w-6 h-6 text-white" data-id="element-2362" data-mp="58qb12rokdu" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-2363" data-mp="362mnp373ca">Contactos</h1>
            </div>
            <p className="text-white/80 text-sm font-medium" data-id="element-2364" data-mp="3rc3501qe9nde">
              Gestión de relaciones y agenda de contactos
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-2365" data-mp="1wxsg5t9sut7m">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-2366" data-mp="hel0hgomz1bs">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2368" data-mp="2on6ul7xtjf7" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2367" data-mp="hgnqi0psgugi">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2370" data-mp="3fxi8vqwzdsot" />} onClick={() => setIsExportModalOpen(true)} isLoading={isExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2369" data-mp="3uredbeq0m16r">
                Exportar Excel
              </Button>}
            {canCreate('contacts') && <Button variant="outline" onClick={openNewForm} className="text-white border-transparent shadow-lg" style={{
            backgroundColor: '#1aa2b2'
          }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#178fa0'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1aa2b2'} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2372" data-mp="2rgfq6faaxnbr" />} data-id="element-2371" data-mp="3f7l24jd4fvgs">
                Nuevo Contacto
              </Button>}
          </div>
        </div>

        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2373" data-mp="2ihloiai466rc" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-400/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" data-id="element-2374" data-mp="2dh7q7k6beb6i" />
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-2375" data-mp="1yfh9mezzwvzk">
          <LockIcon className="w-4 h-4" data-id="element-2376" data-mp="28ccah4jqr8h0" />
          <span data-id="element-2377" data-mp="28dey93lrousp">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            contactos.
          </span>
        </div>}

      {/* View Tabs & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center" data-id="element-2378" data-mp="1fpj7efivpx3u">
        <div className="flex p-1 bg-gray-100 dark:bg-gray-800 rounded-xl" data-id="element-2379" data-mp="1kociy2elve1d">
          <button onClick={() => setViewMode('active')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'active' ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2380" data-mp="1pluk36mdsoyt">
            Activos
          </button>
          <button onClick={() => setViewMode('archived')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'archived' ? 'bg-white dark:bg-gray-700 text-amber-600 dark:text-amber-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2381" data-mp="tw3xbqfwvg5k">
            Archivados
          </button>
          <button onClick={() => setViewMode('deactivated')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'deactivated' ? 'bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2382" data-mp="yp6lfj1x5xed">
            Desactivados
          </button>
        </div>

        <div className="relative w-full md:w-64" data-id="element-2383" data-mp="1wakms1yh3idc">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-2384" data-mp="1w99je38xwcmr" />
          <Input style={{
          paddingLeft: '2.5rem'
        }} placeholder="Buscar contactos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-2385" data-mp="1rb84zvmgl1hm" />
        </div>
      </div>

      {/* Contacts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-id="element-2386" data-mp="1twparxkyjpmn">
        {paginatedContacts.length === 0 ? <div className="col-span-full py-16 text-center" data-id="element-2387" data-mp="2hnuqmcqwehwy">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2388" data-mp="2mja3p2h309sb">
              <ContactIcon className="w-8 h-8 text-gray-400" data-id="element-2389" data-mp="28r3try4iuz1b" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-2390" data-mp="2csyw95y6vigo">
              No hay contactos{' '}
              {viewMode === 'active' ? '' : viewMode === 'archived' ? 'archivados' : 'desactivados'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400" data-id="element-2391" data-mp="2glrif7tl3bv3">
              {searchTerm ? 'Intenta con otra búsqueda' : 'Comienza agregando un nuevo contacto'}
            </p>
          </div> : paginatedContacts.map(contact => <Card key={contact.id} padding="sm" className="group hover:shadow-md transition-all duration-200 border-l-4 border-l-teal-500" data-id="element-2392" data-mp="21x3p7877ne8b">
              <div className="flex items-start justify-between mb-2" data-id="element-2393" data-mp="2ukr5wc5d1kpo">
                <div className="flex items-center gap-2" data-id="element-2394" data-mp="2pkbv9scgqrbe">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm shadow-sm shadow-teal-500/20 flex-shrink-0" data-id="element-2395" data-mp="2pmy0hlbvsuqg">
                    {contact.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0" data-id="element-2396" data-mp="198zhyi8zqt3i">
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate" data-id="element-2397" data-mp="3kuqgzqrpb060">
                      {contact.name}
                    </h3>
                    <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full ${contact.contactStatus === 'nuevo' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : contact.contactStatus === 'contactado' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' : contact.contactStatus === 'interesado' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : contact.contactStatus === 'cita_agendada' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : contact.contactStatus === 'paciente' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : contact.contactStatus === 'perdido' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-2398" data-mp="3pr8misuuv2je">
                      {contact.contactStatus === 'nuevo' ? 'Nuevo' : contact.contactStatus === 'contactado' ? 'Contactado' : contact.contactStatus === 'interesado' ? 'Interesado' : contact.contactStatus === 'cita_agendada' ? 'Cita Agendada' : contact.contactStatus === 'paciente' ? 'Paciente' : contact.contactStatus === 'perdido' ? 'Perdido' : 'Sin estado'}
                    </span>
                  </div>
                </div>

                {/* Actions Menu */}
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2399" data-mp="3qkqn0r1vlhzo">
                  {viewMode === 'active' ? <>
                      {canEdit('contacts') && <button onClick={() => openEditForm(contact)} className="p-1 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-md transition-colors" title="Editar" data-id="element-2400" data-mp="3i9zwyb14mo4m">
                          <EditIcon className="w-3.5 h-3.5" data-id="element-2401" data-mp="9uq1vmvnmj6c" />
                        </button>}
                      {canDelete('contacts') && <>
                          <button onClick={() => openActionConfirm(contact, 'archive')} className="p-1 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-md transition-colors" title="Archivar" data-id="element-2402" data-mp="t0bmkdidqa5">
                            <ArchiveIcon className="w-3.5 h-3.5" data-id="element-2403" data-mp="2i1bgg342tgox" />
                          </button>
                          <button onClick={() => openActionConfirm(contact, 'deactivate')} className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors" title="Desactivar" data-id="element-2404" data-mp="2n1fvrpt905dk">
                            <PowerIcon className="w-3.5 h-3.5" data-id="element-2405" data-mp="242k68rsc4f62" />
                          </button>
                        </>}
                    </> : canDelete('contacts') && <button onClick={() => handleRestore(contact)} className="p-1 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-md transition-colors" title="Restaurar" data-id="element-2406" data-mp="1ucifttcieknz">
                        <RotateCcwIcon className="w-3.5 h-3.5" data-id="element-2407" data-mp="11qk4t54zy1jy" />
                      </button>}
                </div>
              </div>

              <div className="space-y-1 mb-2 text-xs text-gray-600 dark:text-gray-300" data-id="element-2408" data-mp="1aqp8b472kfsu">
                <div className="flex items-center gap-1.5" data-id="element-2409" data-mp="16oc3ci05clx4">
                  <ContactIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-2410" data-mp="16qycgm2or9hg" />
                  <span className="truncate" data-id="element-2411" data-mp="1kn1yvt7bidjd">
                    Origen:{' '}
                    {contact.contactSource === 'whatsapp' ? 'WhatsApp' : contact.contactSource === 'instagram' ? 'Instagram' : contact.contactSource === 'pagina_web' ? 'Página Web' : contact.contactSource === 'google' ? 'Google' : contact.contactSource === 'referido' ? 'Referido' : 'Otro'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-2412" data-mp="1ox47v5ntf4s">
                  <MailIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-2413" data-mp="6gng45k8wayb" />
                  <span className="truncate" data-id="element-2414" data-mp="6irtrf7y4obs">{contact.email}</span>
                </div>
                {contact.phone && <div className="flex items-center gap-1.5" data-id="element-2415" data-mp="39xniqqvdnteo">
                    <PhoneIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-2416" data-mp="39z8dohllted2" />
                    <span data-id="element-2417" data-mp="3exa0ndm5hxtt">{contact.phone}</span>
                  </div>}
              </div>

              {contact.notes && contact.notes.trim() && <div className="bg-yellow-50 dark:bg-yellow-900/10 px-2 py-1.5 rounded-md border border-yellow-100 dark:border-yellow-900/20 mb-2" data-id="element-2418" data-mp="3nxoniherjkzf">
                  <p className="text-[11px] text-gray-600 dark:text-gray-400 italic line-clamp-2" data-id="element-2419" data-mp="y7l631v89hg3">
                    "{contact.notes}"
                  </p>
                </div>}

              {/* Footer info */}
              <div className="border-t border-gray-100 dark:border-gray-700 pt-2 flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500" data-id="element-2420" data-mp="18brzuiqs9bjh">
                <div className="flex items-center gap-1" data-id="element-2421" data-mp="13j8kbg8jc6c1">
                  <CalendarIcon className="w-3 h-3" data-id="element-2422" data-mp="zk9i43e6unoa" />
                  <span data-id="element-2423" data-mp="zhdga3z7zeuk">
                    {new Date(contact.createdAt).toLocaleDateString('es-ES', {
                month: 'short',
                day: 'numeric'
              })}
                  </span>
                </div>
                {contact.createdBy && <div className="flex items-center gap-1" data-id="element-2424" data-mp="ko9zhytgd54w">
                    <UserIcon className="w-3 h-3" data-id="element-2425" data-mp="glnh0oio8wkd" />
                    <span className="truncate max-w-[80px]" data-id="element-2426" data-mp="gmyk0m4am0af">
                      {contact.createdBy}
                    </span>
                  </div>}
              </div>
            </Card>)}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-2427" data-mp="8aepmxgekyaj" />

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedContact ? 'Editar Contacto' : 'Nuevo Contacto'} size="md" icon={<ContactIcon className="w-5 h-5" data-id="element-2429" data-mp="w1sf8vn66frt" />} accentColor="teal" data-id="element-2428" data-mp="3dob4ek8yar8">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2430" data-mp="i215j73orljm">
          {/* Personal Info */}
          <div data-id="element-2431" data-mp="n0lziosch8zm">
            <div className="flex items-center gap-2 mb-4" data-id="element-2432" data-mp="n02s420bm2zu">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-2433" data-mp="n2oxpw3ni8ff"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2434" data-mp="32qf9z1gfv1c8">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2435" data-mp="2z915snszi068">
              <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Nombre del contacto" data-id="element-2436" data-mp="2vajz25dh6rpc" />
              <div className="grid grid-cols-2 gap-4" data-id="element-2437" data-mp="2bm2t817zjcdl">
                <Select label="Estado del Contacto" options={[{
                value: 'nuevo',
                label: 'Nuevo'
              }, {
                value: 'contactado',
                label: 'Contactado'
              }, {
                value: 'interesado',
                label: 'Interesado'
              }, {
                value: 'cita_agendada',
                label: 'Cita Agendada'
              }, {
                value: 'paciente',
                label: 'Paciente'
              }, {
                value: 'perdido',
                label: 'Perdido'
              }]} value={formData.contactStatus} onChange={value => setFormData({
                ...formData,
                contactStatus: value as ContactStatus
              })} data-id="element-2438" data-mp="2q9wdw0bxh531" />
                <Select label="Origen del Contacto" options={[{
                value: 'whatsapp',
                label: 'WhatsApp'
              }, {
                value: 'instagram',
                label: 'Instagram'
              }, {
                value: 'pagina_web',
                label: 'Página Web'
              }, {
                value: 'google',
                label: 'Google'
              }, {
                value: 'referido',
                label: 'Referido'
              }, {
                value: 'otro',
                label: 'Otro'
              }]} value={formData.contactSource} onChange={value => setFormData({
                ...formData,
                contactSource: value as ContactSource
              })} data-id="element-2439" data-mp="250acc0rmqq9g" />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div data-id="element-2440" data-mp="21nmsqz2z7479">
            <div className="flex items-center gap-2 mb-4" data-id="element-2441" data-mp="21p7mwnldol4l">
              <span className="w-1.5 h-5 rounded-full bg-cyan-500" data-id="element-2442" data-mp="1wpl8iattcyu7"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2443" data-mp="1wro7hkcy22b2">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2444" data-mp="25ogdxkp1a4wg">
              <div className="grid grid-cols-2 gap-4" data-id="element-2445" data-mp="2kkxqgf8ylpkl">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} placeholder="809-000-0000" data-id="element-2446" data-mp="2fneanawm54qj" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="contacto@email.com" data-id="element-2447" data-mp="2yep2bua5xljh" />
              </div>
              <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} rows={3} placeholder="Notas adicionales sobre el contacto..." data-id="element-2448" data-mp="3czjb216gpcdv" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-2449" data-mp="2gbdc9uw81e7i">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-2450" data-mp="2kcgqye2j9475">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-2451" data-mp="m39jh6u59j47">
              {selectedContact ? 'Guardar Cambios' : 'Crear Contacto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Contactos" isExporting={isExporting} data-id="element-2452" data-mp="c5d8qg54cq6m" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Contactos a PDF" isExporting={isPdfExporting} data-id="element-2453" data-mp="14uk63bsu79qa" />
    </div>;
}